import {
  PRODUCT_ROUTES,
  productDeletedResponseSchema,
  productListResponseSchema,
  productResponseSchema,
  productUpdateSchema,
  type Product,
  type ProductInput,
  type ProductList,
  type ProductListQuery,
  type ProductUpdate,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";

/**
 * El catálogo visto desde el panel.
 *
 * Todas las llamadas pasan por `authFetch`: adjunta el access token,
 * renueva una vez si expiró y cierra la sesión si el token es inválido.
 * Ninguna pantalla de este módulo toca `fetch` a mano.
 *
 * `baseUrl: ""` porque `PRODUCT_ROUTES` YA trae el prefijo `/api`. Sin
 * esto quedaría `/api/api/admin/products` y el 404 se descubriría en
 * ejecución, no al compilar.
 */
const NO_PREFIX = { baseUrl: "" } as const;

/** Lo que el listado del panel sabe pedir. */
export type AdminProductsQuery = ProductListQuery;

/**
 * La URL del listado con sus filtros.
 *
 * El orden de los parámetros es FIJO y no depende del orden en que se
 * tocaron los controles: así dos estados idénticos producen la misma URL,
 * react-query los reconoce como la misma entrada de caché y las pruebas
 * pueden responder por ruta exacta.
 */
export function adminProductsUrl(query: AdminProductsQuery): string {
  const params = new URLSearchParams();
  if (query.search) params.set("search", query.search);
  if (query.category) params.set("category", query.category);
  if (query.status) params.set("status", query.status);
  params.set("page", String(query.page));
  params.set("perPage", String(query.perPage));
  params.set("sort", query.sort);
  return `${PRODUCT_ROUTES.adminList}?${params.toString()}`;
}

export function listAdminProducts(query: AdminProductsQuery): Promise<ProductList> {
  return authFetch(adminProductsUrl(query), productListResponseSchema, NO_PREFIX);
}

export async function getAdminProduct(id: string): Promise<Product> {
  const data = await authFetch(
    PRODUCT_ROUTES.adminOne(id),
    productResponseSchema,
    NO_PREFIX,
  );
  return data.product;
}

export async function createProduct(input: ProductInput): Promise<Product> {
  const data = await authFetch(PRODUCT_ROUTES.adminCreate, productResponseSchema, {
    ...NO_PREFIX,
    method: "POST",
    body: input,
  });
  return data.product;
}

/**
 * Editar. El `slug` NO viaja: `productUpdateSchema` lo omite por contrato,
 * y aquí se vuelve a pasar por el esquema antes de enviar para que un
 * campo de más —o mal formado— se detecte aquí y no en el servidor.
 *
 * Después de validar se envían SOLO las claves que venían en `patch`.
 * `productUpdateSchema` es `productInputSchema.partial()`, y los campos
 * con `.default()` del contrato (`status`, `sortOrder`) siguen aplicando
 * su valor por omisión aunque la clave no esté: sin este filtro,
 * archivar un producto mandaría también `sortOrder: 0` y le borraría al
 * catálogo el orden que alguien colocó a mano.
 */
export async function updateProduct(id: string, patch: ProductUpdate): Promise<Product> {
  const validated = productUpdateSchema.parse(patch) as Record<string, unknown>;
  const body = Object.fromEntries(
    Object.keys(patch).map((key) => [key, validated[key]]),
  );
  const data = await authFetch(PRODUCT_ROUTES.adminOne(id), productResponseSchema, {
    ...NO_PREFIX,
    method: "PATCH",
    body,
  });
  return data.product;
}

/**
 * Archivar.
 *
 * NO existe un borrado de verdad y no se pide uno: un producto borrado
 * deja cada pedido histórico apuntando a la nada, y el historial de
 * compras es justo lo que no se puede perder. Archivar lo saca del
 * catálogo público y lo deja intacto para los pedidos que lo mencionan.
 *
 * El verbo es DELETE porque es la acción que se pulsa —"quítalo de la
 * tienda"— y porque el servidor la interpreta como archivar: pasa el
 * producto a `archived` y conserva el documento. La respuesta dice
 * `deleted: true`, que es verdad para quien lo pulsó y mentira para la
 * base de datos; por eso el contrato le da su propio esquema y por eso
 * este módulo llama a la función `archiveProduct` y no `deleteProduct`.
 */
export async function archiveProduct(id: string): Promise<void> {
  await authFetch(PRODUCT_ROUTES.adminOne(id), productDeletedResponseSchema, {
    ...NO_PREFIX,
    method: "DELETE",
  });
}
